import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Loader } from "google-maps";
import { config } from "~/core";
import { apiScheduler } from "../api/scheduler";
import { apiInspections } from "../api/inspections";
import { apiAutoSchedule } from "../api/autoschedule";
import { apiWorkAreas } from "../api/workareas";
import { metersToMiles } from "../utilities/inspections";
import { formatTime } from "../utilities/dateTime";
import { toast, ToastContainer } from "react-toastify";
import { useTour } from "../tours/useTour";

// ── date helpers (Mon-start week) ──────────────────────────────────────────
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
function mondayOf(date) {
  const jsd = date.getDay();
  const m = startOfDay(date);
  m.setDate(m.getDate() - (jsd === 0 ? 6 : jsd - 1));
  return m;
}
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAY_SHORT = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];
  let startPad = firstDay.getDay() - 1; if (startPad < 0) {startPad = 6;}
  for (let i = startPad - 1; i >= 0; i--) {days.push({ date: new Date(year, month, -i), isCurrentMonth: false });}
  for (let d = 1; d <= lastDay.getDate(); d++) {days.push({ date: new Date(year, month, d), isCurrentMonth: true });}
  while (days.length % 7 !== 0) {
    const last = days[days.length - 1].date;
    days.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1), isCurrentMonth: false });
  }
  return days;
}

// ── area color palette (literal class strings so purgecss keeps them) ───────
const PALETTE = [
  { block: "tw-bg-blue-100 tw-border-blue-400 tw-text-blue-800", dot: "tw-bg-blue-400" },
  { block: "tw-bg-amber-100 tw-border-amber-400 tw-text-amber-800", dot: "tw-bg-amber-400" },
  { block: "tw-bg-rose-100 tw-border-rose-400 tw-text-rose-800", dot: "tw-bg-rose-400" },
  { block: "tw-bg-teal-100 tw-border-teal-400 tw-text-teal-800", dot: "tw-bg-teal-400" },
  { block: "tw-bg-indigo-100 tw-border-indigo-400 tw-text-indigo-800", dot: "tw-bg-indigo-400" },
  { block: "tw-bg-fuchsia-100 tw-border-fuchsia-400 tw-text-fuchsia-800", dot: "tw-bg-fuchsia-400" },
  { block: "tw-bg-cyan-100 tw-border-cyan-400 tw-text-cyan-800", dot: "tw-bg-cyan-400" },
  { block: "tw-bg-orange-100 tw-border-orange-400 tw-text-orange-800", dot: "tw-bg-orange-400" },
  { block: "tw-bg-emerald-100 tw-border-emerald-400 tw-text-emerald-800", dot: "tw-bg-emerald-400" },
  { block: "tw-bg-violet-100 tw-border-violet-400 tw-text-violet-800", dot: "tw-bg-violet-400" },
  { block: "tw-bg-sky-100 tw-border-sky-400 tw-text-sky-800", dot: "tw-bg-sky-400" },
  { block: "tw-bg-lime-100 tw-border-lime-400 tw-text-lime-800", dot: "tw-bg-lime-400" },
];
const NEUTRAL = { block: "tw-bg-gray-100 tw-border-gray-300 tw-text-gray-700", dot: "tw-bg-gray-400" };
const AVAILABLE = "tw-bg-green-50 tw-border-green-400 tw-border-dashed tw-text-green-800";
const SIMULATED = "tw-bg-purple-50 tw-border-purple-400 tw-text-purple-900";

function fullnessBarColor(pct) {
  if (pct >= 90) {return "tw-bg-red-500";}
  if (pct >= 70) {return "tw-bg-amber-500";}
  return "tw-bg-green-500";
}
function pctLabel(pct) { return pct > 0 ? `${Math.round(pct)}%` : ""; }

// ── route / available block ─────────────────────────────────────────────────
function RouteBlock({ entry, dateStr, areaColor, compact, onOpenRoute }) {
  const plannerUrl = `/planner?inspector=${entry.userId}&date=${dateStr}`;
  const style = entry.workAreaId && areaColor[entry.workAreaId] || NEUTRAL;

  if (entry.status === "available") {
    return (
      <div title={`${entry.name} — available${entry.workAreaName ? " · " + entry.workAreaName : ""}`}
        className={`tw-w-full tw-text-left tw-px-1.5 tw-py-0.5 tw-rounded tw-border tw-text-xs tw-leading-tight ${AVAILABLE}`}>
        <span className="tw-font-medium">{entry.name}</span>
        {entry.workAreaName && <span className="tw-opacity-70"> · {entry.workAreaName}</span>}
      </div>
    );
  }

  if (entry.status === "appointments") {
    return (
      <button onClick={() => window.open(plannerUrl, "_blank")} title={`${entry.name} — ${entry.apptCount} appointment${entry.apptCount === 1 ? "" : "s"}, no route`}
        className={`tw-w-full tw-text-left tw-px-1.5 tw-py-0.5 tw-rounded tw-border tw-text-xs tw-leading-tight ${style.block}`}>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-1">
          <span className="tw-font-medium tw-truncate">{entry.name}</span>
          <span className="tw-shrink-0 tw-font-bold tw-text-[9px] tw-uppercase">No route</span>
        </div>
        <div className="tw-truncate tw-opacity-70 tw-text-[10px]">{entry.apptCount} appointment{entry.apptCount === 1 ? "" : "s"}{entry.workAreaName ? " · " + entry.workAreaName : ""}{entry.predictionCount > 0 ? ` · ⚡${entry.predictionCount} predicted` : ""}</div>
      </button>
    );
  }

  if (entry.status === "simulated") {
    const r = entry.route || {};
    return (
      <button onClick={() => onOpenRoute(entry, dateStr)}
        title={`${entry.name} — SIMULATED · ${Math.round(r.fullnessPct||0)}% full · ${r.stopCount||0} inspections (real + auto-schedule offers)`}
        className={`tw-w-full tw-text-left tw-px-1.5 tw-py-0.5 tw-rounded tw-border tw-text-xs tw-leading-tight ${SIMULATED}`}>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-1">
          <span className="tw-font-medium tw-truncate">{entry.name}</span>
          <span className="tw-shrink-0 tw-font-bold tw-text-[9px] tw-uppercase tw-text-purple-700">Sim</span>
        </div>
        <div className="tw-mt-0.5 tw-h-1 tw-w-full tw-rounded-full tw-bg-black/10 tw-overflow-hidden">
          <div className="tw-h-full tw-bg-purple-500" style={{ width: `${Math.min(100, Math.max(0, r.fullnessPct||0))}%` }} />
        </div>
        {!compact && <div className="tw-mt-0.5 tw-text-[10px] tw-opacity-80">{pctLabel(r.fullnessPct)} · {r.stopCount||0} inspections</div>}
      </button>
    );
  }

  const r = entry.route || {};
  const isFull = r.fullnessPct >= 90;
  const hasUnrouted = r.unroutedCount > 0;
  return (
    <button onClick={() => onOpenRoute(entry, dateStr)}
      title={`${entry.name} — ${entry.workAreaName || "no area"} · ${Math.round(r.fullnessPct||0)}% full · ${r.stopCount||0} inspections${hasUnrouted ? ` · ${r.unroutedCount} unrouted` : ""}`}
      className={`tw-w-full tw-text-left tw-px-1.5 tw-py-0.5 tw-rounded tw-border tw-text-xs tw-leading-tight ${style.block}`}>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-1">
        <span className="tw-font-medium tw-truncate">{entry.name}</span>
        <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
          {isFull && <span className="tw-font-bold tw-text-[9px] tw-uppercase">FULL</span>}
          {hasUnrouted && <span className="tw-bg-red-500 tw-text-white tw-rounded tw-px-1 tw-py-0.5 tw-text-[9px] tw-font-bold tw-leading-none" title={`${r.unroutedCount} inspection${r.unroutedCount === 1 ? "" : "s"} couldn't fit on the route`}>⚠ {r.unroutedCount}</span>}
          {entry.predictionCount > 0 && <span className="tw-bg-purple-500 tw-text-white tw-rounded tw-px-1 tw-py-0.5 tw-text-[9px] tw-font-bold tw-leading-none" title={`${entry.predictionCount} auto-schedule prediction${entry.predictionCount === 1 ? "" : "s"} (dry-run)`}>⚡ {entry.predictionCount}</span>}
        </div>
      </div>
      {!compact && entry.workAreaName && <div className="tw-truncate tw-opacity-70 tw-text-[10px]">{entry.workAreaName}</div>}
      {/* fullness bar */}
      <div className="tw-mt-0.5 tw-h-1 tw-w-full tw-rounded-full tw-bg-black/10 tw-overflow-hidden">
        <div className={`tw-h-full ${fullnessBarColor(r.fullnessPct||0)}`} style={{ width: `${Math.min(100, Math.max(0, r.fullnessPct||0))}%` }} />
      </div>
      {!compact && 
        <div className="tw-mt-0.5 tw-flex tw-items-center tw-justify-between tw-text-[10px] tw-opacity-80">
          <span>{pctLabel(r.fullnessPct)} · {r.stopCount||0} inspections</span>
        </div>
      }
    </button>
  );
}

// ── planned route map (numbered stops + home marker + connecting line) ──────
function RouteMap({ route }) {
  const containerRef = useRef(null);
  const [loader] = useState(() => new Loader(config.mapsKeyPlanner, { version: "weekly", libraries: ["geometry"] }));
  useEffect(() => {
    const stops = route?.RouteInspections || [];
    if (!containerRef.current || stops.length === 0) {return;}
    let cancelled = false;
    (async () => {
      const google = await loader.load();
      if (cancelled || !containerRef.current) {return;}
      const map = new google.maps.Map(containerRef.current, {
        center: { lat: stops[0].Lat, lng: stops[0].Lng },
        zoom: 10,
        mapId: "scheduler-route-map",
        streetViewControl: false,
      });
      const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
      if (cancelled) {return;}
      const bounds = new google.maps.LatLngBounds();
      let homePos = null;
      if (route.StartLat || route.StartLong) {
        homePos = { lat: route.StartLat, lng: route.StartLong };
        const tag = document.createElement("div");
        tag.textContent = "🏠";
        tag.style.fontSize = "1.1rem";
        new AdvancedMarkerElement({ map, position: homePos, content: tag });
        bounds.extend(homePos);
      }
      let endPos = null;
      if (route.EndLat || route.EndLong) {
        endPos = { lat: route.EndLat, lng: route.EndLong };
        if (!homePos || endPos.lat !== homePos.lat || endPos.lng !== homePos.lng) {
          const tag = document.createElement("div");
          tag.textContent = "🏁";
          tag.style.fontSize = "1.1rem";
          new AdvancedMarkerElement({ map, position: endPos, content: tag });
          bounds.extend(endPos);
        }
      }
      stops.forEach((s, i) => {
        const pos = { lat: s.Lat, lng: s.Lng };
        const tag = document.createElement("div");
        tag.textContent = String(i + 1);
        tag.style.background = "#1d4ed8";
        tag.style.color = "#fff";
        tag.style.fontWeight = "700";
        tag.style.padding = "3px 8px";
        tag.style.borderRadius = "9999px";
        tag.style.fontSize = "0.72rem";
        new AdvancedMarkerElement({ map, position: pos, content: tag });
        bounds.extend(pos);
      });
      // actual driving route: VROOM whole-route geometry, else Google per-leg polylines, else straight lines.
      // Gated behind arcpro-route-polygons so the route line can be revealed on demand.
      if (config.routePolygonsEnabled) {
        const path = [];
        if (route.RoutePolyline) {
          path.push(...google.maps.geometry.encoding.decodePath(route.RoutePolyline));
        } else {
          if (homePos) { path.push(homePos); }
          stops.forEach((s) => {
            if (s.Polylines) { path.push(...google.maps.geometry.encoding.decodePath(s.Polylines)); }
            else { path.push({ lat: s.Lat, lng: s.Lng }); }
          });
          if (endPos) { path.push(endPos); } else if (homePos) { path.push(homePos); }
        }
        new google.maps.Polyline({ path, map, strokeColor: "#1d4ed8", strokeOpacity: 0.75, strokeWeight: 7 });
      }
      if (!bounds.isEmpty()) {map.fitBounds(bounds);}
    })();
    return () => { cancelled = true; };
  }, [route, loader]);
  return <div ref={containerRef} className="tw-w-full tw-h-96 tw-rounded tw-border tw-bg-gray-100" />;
}

// ── page ────────────────────────────────────────────────────────────────────

export default function SchedulerCalendar() {
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(startOfDay(new Date()));
  const [workAreas, setWorkAreas] = useState([]);
  const [hiddenAreas, setHiddenAreas] = useState(() => new Set());
  const [dayMap, setDayMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [dayDetail, setDayDetail] = useState(null); // { date, entries }
  const [routeDetail, setRouteDetail] = useState(null); // { entry, dateStr, route, loading, error }
  const [saving, setSaving] = useState(false);
  const [showSimulation, setShowSimulation] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simRoutes, setSimRoutes] = useState([]);
  const toggledBeforeLoad = useRef(false);

  const loadWorkAreas = useCallback(() => {
    apiWorkAreas.list().then((res) => setWorkAreas(res.data.result || [])).catch(() => {});
  }, []);
  useEffect(() => { loadWorkAreas(); }, [loadWorkAreas]);
  useEffect(() => {
    apiScheduler.getPreferences()
      .then((res) => { if (!toggledBeforeLoad.current) {setHiddenAreas(new Set(res.data.result?.hiddenAreas || []));} })
      .catch(() => {});
  }, []);

  // stable areaId -> palette index
  const areaColor = useMemo(() => {
    const map = {};
    [...workAreas].sort((a, b) => (a.name || "").localeCompare(b.name || "")).forEach((a, i) => { map[a.id] = PALETTE[i % PALETTE.length]; });
    return map;
  }, [workAreas]);

  const visibleDays = useMemo(() => {
    if (view === "day") {return [{ date: startOfDay(anchor), isCurrentMonth: true }];}
    if (view === "week") {
      const mon = mondayOf(anchor);
      return Array.from({ length: 7 }, (_, i) => ({ date: addDays(mon, i), isCurrentMonth: true }));
    }
    return getCalendarDays(anchor.getFullYear(), anchor.getMonth());
  }, [view, anchor]);

  const from = formatDate(visibleDays[0].date);
  const to = formatDate(visibleDays[visibleDays.length - 1].date);

  useEffect(() => {
    setLoading(true);
    apiScheduler.getCalendar(from, to)
      .then((res) => {
        const map = {};
        (res.data.result?.days || []).forEach((d) => { map[d.date] = d; });
        setDayMap(map);
      })
      .catch(() => toast.error("Error loading calendar"))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => {
    if (!showSimulation) { setSimRoutes([]); return; }
    let cancelled = false;
    apiAutoSchedule.getSimulatedRoutes(from, to)
      .then((res) => { if (!cancelled) {setSimRoutes(res.data.result || []);} })
      .catch(() => { if (!cancelled) {toast.error("Error loading simulated routes");} });
    return () => { cancelled = true; };
  }, [showSimulation, from, to]);

  // fetch the full route (with stops + distance) when a route popup is opened
  const runSimulation = useCallback(() => {
    setSimulating(true);
    apiAutoSchedule.runSimulation()
      .then(() => {
        toast.success("Simulation started — refresh in a moment to see the routes.");
        setTimeout(() => {
          apiAutoSchedule.getSimulatedRoutes(from, to)
            .then((res) => setSimRoutes(res.data.result || []))
            .finally(() => setSimulating(false));
        }, 4000);
      })
      .catch(() => { toast.error("Error starting simulation"); setSimulating(false); });
  }, [from, to]);

  const openRoute = useCallback((entry, dateStr) => {
    if (entry.status === "simulated" && entry._simRoute) {
      setRouteDetail({ entry, dateStr, route: entry._simRoute, loading: false, error: false });
    } else {
      setRouteDetail({ entry, dateStr, route: null, loading: true, error: false });
    }
  }, []);
  useEffect(() => {
    if (!routeDetail || routeDetail.route || routeDetail.error) {return;}
    let cancelled = false;
    apiInspections.getRoute(routeDetail.entry.userId, routeDetail.dateStr)
      .then((res) => { if (!cancelled) {setRouteDetail((d) => ({ ...d, route: res.data.result, loading: false }));} })
      .catch(() => { if (!cancelled) {setRouteDetail((d) => ({ ...d, error: true, loading: false }));} });
    return () => { cancelled = true; };
  }, [routeDetail && routeDetail.entry && routeDetail.entry.userId, routeDetail && routeDetail.dateStr]);

  const toggleArea = (id) => {
    toggledBeforeLoad.current = true;
    setHiddenAreas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {next.delete(id);} else {next.add(id);}
      return next;
    });
  };

  const saveDefault = () => {
    setSaving(true);
    apiScheduler.savePreferences([...hiddenAreas])
      .then((res) => {
        if (res.data.status === 200) {toast.success("Work area filter saved as default");}
        else {toast.error("Could not save default");}
      })
      .catch(() => toast.error("Could not save default"))
      .finally(() => setSaving(false));
  };

  const isHidden = (entry) => entry.workAreaId !== 0 && hiddenAreas.has(entry.workAreaId);
  const todayStr = formatDate(new Date());

  // navigation
  const shift = (dir) => {
    if (view === "month") {setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));}
    else if (view === "week") {setAnchor(addDays(anchor, dir * 7));}
    else {setAnchor(addDays(anchor, dir));}
  };

  const label = useMemo(() => {
    if (view === "month") {return `${MONTH_NAMES[anchor.getMonth()]} ${anchor.getFullYear()}`;}
    if (view === "day") {return anchor.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });}
    const mon = mondayOf(anchor), sun = addDays(mon, 6);
    const sameMo = mon.getMonth() === sun.getMonth();
    return sameMo
      ? `${MONTH_NAMES[mon.getMonth()].slice(0, 3)} ${mon.getDate()}–${sun.getDate()}, ${sun.getFullYear()}`
      : `${MONTH_NAMES[mon.getMonth()].slice(0, 3)} ${mon.getDate()} – ${MONTH_NAMES[sun.getMonth()].slice(0, 3)} ${sun.getDate()}, ${sun.getFullYear()}`;
  }, [view, anchor]);

  const tourSteps = useMemo(() => [
    {
      element: '[data-tour="sc-title"]',
      popover: {
        title: "Scheduler Calendar",
        description: "This is the scheduling calendar — see which inspector is routed where, each day. Let's walk through it.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour="sc-view"]',
      popover: {
        title: "Switch the view",
        description: "Switch between Day, Week and Month to zoom in or out on the schedule.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour="sc-date"]',
      popover: {
        title: "Move around",
        description: "Step backward and forward, or jump straight back to Today.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour="sc-areas"]',
      popover: {
        title: "Work areas",
        description: "Filter the calendar by work area. Tick/untick areas, then 'Save as default' to remember your view.",
        side: "right", align: "start",
      },
    },
    {
      element: '[data-tour="sc-legend"]',
      popover: {
        title: "Reading the blocks",
        description: "Each route block shows its fullness bar, 'FULL', and ⚠ for inspections that couldn't fit on the route.",
        side: "right", align: "start",
      },
    },
    {
      element: '[data-tour="sc-grid"]',
      popover: {
        title: "Drill into a route",
        description: "Click any route block to see its map, distance and times — then 'Open in Planner' to edit it.",
        side: "top", align: "start",
      },
    },
  ], []);

  const { startTour, showButton } = useTour({ tourKey: "scheduler-calendar", steps: tourSteps });

  const simEntriesFor = (dateStr) => {
    if (!showSimulation) {return [];}
    return simRoutes
      .filter((r) => (r.date || "").slice(0, 10) === dateStr)
      .map((r) => {
        let route = null;
        try { route = JSON.parse(r.routeData); } catch (e) { /* not valid JSON */ }
        return { userId: r.userId, name: r.inspectorName || `inspector ${r.userId}`, status: "simulated", route: { fullnessPct: r.fullnessPct, stopCount: r.stopCount }, _simRoute: route };
      });
  };

  const entriesFor = (dateStr) => {
    const d = dayMap[dateStr];
    const real = d ? (d.entries || []).filter((e) => !isHidden(e)) : [];
    return [...real, ...simEntriesFor(dateStr)];
  };

  return (
    <div className="tw-max-w-[1400px] tw-mx-auto tw-p-6">
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar closeOnClick />
      <h1 data-tour="sc-title" className="tw-text-2xl tw-font-bold tw-mb-4">Scheduler Calendar</h1>

      {/* toolbar */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mb-4">
        <div data-tour="sc-view" className="tw-inline-flex tw-rounded tw-border tw-border-gray-300 tw-overflow-hidden">
          {["day", "week", "month"].map((v) => 
            <button key={v} onClick={() => setView(v)}
              className={`tw-px-3 tw-py-1.5 tw-text-sm ${view === v ? "tw-bg-blue-600 tw-text-white" : "tw-bg-white hover:tw-bg-gray-50"}`}>
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          )}
        </div>
        <div data-tour="sc-date" className="tw-flex tw-items-center tw-gap-2">
          <button onClick={() => shift(-1)} className="tw-px-2.5 tw-py-1 tw-border tw-rounded hover:tw-bg-gray-50">&lt;</button>
          <button onClick={() => setAnchor(startOfDay(new Date()))} className="tw-px-3 tw-py-1 tw-border tw-rounded hover:tw-bg-gray-50 tw-text-sm">Today</button>
          <button onClick={() => shift(1)} className="tw-px-2.5 tw-py-1 tw-border tw-rounded hover:tw-bg-gray-50">&gt;</button>
          <span className="tw-font-medium tw-text-lg tw-ml-2">{label}</span>
        </div>
        {loading && <span className="tw-text-gray-400 tw-text-sm">Loading…</span>}
        {showButton &&
          <button onClick={startTour} className="tw-ml-auto tw-px-3 tw-py-1.5 tw-border tw-rounded tw-text-sm hover:tw-bg-gray-50">Take the tour</button>
        }
        {config.isSuperAdmin && 
          <>
            <button onClick={runSimulation} disabled={simulating}
              className="tw-ml-auto tw-px-3 tw-py-1.5 tw-border tw-rounded tw-text-sm hover:tw-bg-gray-50 disabled:tw-opacity-50">
              {simulating ? "Simulating…" : "Run simulation"}
            </button>
            <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-cursor-pointer">
              <input type="checkbox" checked={showSimulation} onChange={(e) => setShowSimulation(e.target.checked)} />
              Show simulation
            </label>
          </>
        }
      </div>

      <div className="tw-flex tw-gap-6">
        {/* sidebar: area filters + legend */}
        <aside data-tour="sc-areas" className="tw-w-52 tw-shrink-0">
          <h2 className="tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-500 tw-mb-2">Work areas</h2>
          <div className="tw-space-y-1 tw-mb-4">
            {workAreas.length === 0 && <p className="tw-text-xs tw-text-gray-400">No work areas</p>}
            {[...workAreas].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((a) => {
              const c = areaColor[a.id] || NEUTRAL;
              const hidden = hiddenAreas.has(a.id);
              return (
                <label key={a.id} className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-cursor-pointer">
                  <input type="checkbox" checked={!hidden} onChange={() => toggleArea(a.id)} className="tw-mr-1" />
                  <span className={`tw-w-3 tw-h-3 tw-rounded ${c.dot}`} />
                  <span className={hidden ? "tw-line-through tw-text-gray-400" : ""}>{a.name}</span>
                </label>
              );
            })}
          </div>
          <button onClick={saveDefault} disabled={saving} className="tw-mb-4 tw-text-xs tw-text-blue-600 hover:tw-underline disabled:tw-opacity-50">{saving ? "Saving…" : "Save as default"}</button>

          <h2 className="tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-500 tw-mb-2">Legend</h2>
          <div data-tour="sc-legend" className="tw-space-y-1.5 tw-text-xs tw-text-gray-600">
            <div className="tw-flex tw-items-center tw-gap-2"><span className="tw-w-4 tw-h-3 tw-rounded tw-bg-blue-100 tw-border tw-border-blue-400" /> Route</div>
            <div className="tw-flex tw-items-center tw-gap-2"><span className="tw-w-4 tw-h-3 tw-rounded tw-border tw-border-gray-300 tw-bg-gray-50" /> Appointments, no route (NO ROUTE label)</div>
            <div className="tw-flex tw-items-center tw-gap-2"><span className="tw-w-4 tw-h-3 tw-rounded tw-bg-green-50 tw-border tw-border-green-400 tw-border-dashed" /> Available (no route)</div>
            <div className="tw-flex tw-items-center tw-gap-2"><span className="tw-w-4 tw-h-1.5 tw-rounded-full tw-bg-green-500" />&lt;70%</div>
            <div className="tw-flex tw-items-center tw-gap-2"><span className="tw-w-4 tw-h-1.5 tw-rounded-full tw-bg-amber-500" />70–90%</div>
            <div className="tw-flex tw-items-center tw-gap-2"><span className="tw-w-4 tw-h-1.5 tw-rounded-full tw-bg-red-500" />≥90% (FULL)</div>
            <div className="tw-flex tw-items-center tw-gap-2"><span className="tw-bg-red-500 tw-text-white tw-rounded tw-px-1 tw-text-[9px] tw-font-bold tw-leading-none">⚠N</span> N inspections couldn&apos;t fit</div>
            <div className="tw-flex tw-items-center tw-gap-2"><span className="tw-w-4 tw-h-3 tw-rounded tw-bg-purple-50 tw-border tw-border-purple-400" /> Simulated route (auto-schedule what-if)</div>
          </div>
        </aside>

        {/* grid */}
        <div data-tour="sc-grid" className="tw-flex-1 tw-min-w-0">
          {view === "day" && <DayView date={visibleDays[0].date} entries={entriesFor(formatDate(visibleDays[0].date))} areaColor={areaColor} todayStr={todayStr} onOpenRoute={openRoute} />}
          {view === "week" && <WeekView days={visibleDays} entriesFor={entriesFor} areaColor={areaColor} todayStr={todayStr} onOpenRoute={openRoute} />}
          {view === "month" && <MonthView days={visibleDays} entriesFor={entriesFor} areaColor={areaColor} todayStr={todayStr} onMore={setDayDetail} onOpenRoute={openRoute} />}
        </div>
      </div>

      {/* month overflow popover */}
      {dayDetail && 
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-start tw-justify-center tw-z-50 tw-pt-20" onClick={() => setDayDetail(null)}>
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-md tw-max-h-[70vh] tw-overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b">
              <h3 className="tw-font-semibold">{new Date(dayDetail.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</h3>
              <button onClick={() => setDayDetail(null)} className="tw-text-gray-400 hover:tw-text-gray-700">✕</button>
            </div>
            <div className="tw-p-3 tw-space-y-1.5">
              {dayDetail.entries.length === 0 && <p className="tw-text-sm tw-text-gray-400">No inspectors</p>}
              {dayDetail.entries.map((e) => 
                <RouteBlock key={`${e.status}-${e.userId}`} entry={e} dateStr={dayDetail.date} areaColor={areaColor} compact={false} onOpenRoute={openRoute} />
              )}
            </div>
          </div>
        </div>
      }

      {/* route detail popup */}
      {routeDetail && 
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-start tw-justify-center tw-z-50 tw-pt-10" onClick={() => setRouteDetail(null)}>
          <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-2xl tw-max-h-[88vh] tw-overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b">
              <div>
                <h3 className="tw-font-semibold tw-text-lg">{routeDetail.entry.name}</h3>
                <div className="tw-text-xs tw-text-gray-500">{routeDetail.entry.workAreaName || "no area"} · {new Date(routeDetail.dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
              </div>
              <button onClick={() => setRouteDetail(null)} className="tw-text-gray-400 hover:tw-text-gray-700">✕</button>
            </div>
            <div className="tw-px-4 tw-py-3">
              {routeDetail.loading && <p className="tw-text-gray-400">Loading route…</p>}
              {routeDetail.error && <p className="tw-text-red-600">Error loading route.</p>}
              {routeDetail.route && 
                <>
                  <div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-1 tw-mb-3 tw-text-sm">
                    <div><span className="tw-text-gray-500">Start:</span> <span className="tw-font-medium">{formatTime(new Date(routeDetail.route.StartTime))}</span></div>
                    <div><span className="tw-text-gray-500">End:</span> <span className="tw-font-medium">{formatTime(new Date(routeDetail.route.EndTime))}</span></div>
                    <div><span className="tw-text-gray-500">Distance:</span> <span className="tw-font-medium">{metersToMiles(routeDetail.route.Distance || 0)} mi</span></div>
                    <div><span className="tw-text-gray-500">Fullness:</span> <span className="tw-font-medium">{Math.round(routeDetail.entry.route?.fullnessPct || 0)}%</span></div>
                    <div><span className="tw-text-gray-500">Inspections:</span> <span className="tw-font-medium">{routeDetail.route.RouteInspections?.length || 0}</span></div>
                    {routeDetail.entry.route?.unroutedCount > 0 && <div className="tw-text-red-600"><span className="tw-text-gray-500">Unrouted:</span> <span className="tw-font-medium">{routeDetail.entry.route.unroutedCount}</span></div>}
                  </div>
                  <RouteMap route={routeDetail.route} />
                  <div className="tw-mt-3 tw-flex tw-justify-end">
                    <a className="tw-px-4 tw-py-2 tw-rounded tw-bg-blue-600 tw-text-white tw-text-sm hover:tw-bg-blue-700" target="_blank" rel="noopener noreferrer" href={`/planner?inspector=${routeDetail.entry.userId}&date=${routeDetail.dateStr}${routeDetail.entry.status === "simulated" ? "&simulated=1" : ""}`}>Open in Planner →</a>
                  </div>
                </>
              }
            </div>
          </div>
        </div>
      }
    </div>
  );
}

// ── month ───────────────────────────────────────────────────────────────────
function MonthView({ days, entriesFor, areaColor, todayStr, onMore, onOpenRoute }) {
  return (
    <div className="tw-border tw-rounded tw-overflow-hidden">
      <div className="tw-grid tw-grid-cols-7 tw-bg-gray-100">
        {WEEKDAY_SHORT.map((d) => 
          <div key={d} className="tw-text-center tw-text-xs tw-font-medium tw-text-gray-600 tw-py-2">{d}</div>
        )}
      </div>
      <div className="tw-grid tw-grid-cols-7">
        {days.map((day, i) => {
          const ds = formatDate(day.date);
          const entries = entriesFor(ds);
          const routed = entries.filter((e) => e.status === "routed");
          const appts = entries.filter((e) => e.status === "appointments");
          const available = entries.filter((e) => e.status === "available");
          const simulated = entries.filter((e) => e.status === "simulated");
          const isToday = ds === todayStr;
          return (
            <div key={i} className={`tw-min-h-[5.5rem] tw-p-1 tw-border-t tw-border-l ${i % 7 === 6 ? "" : "tw-border-r"} ${day.isCurrentMonth ? "tw-bg-white" : "tw-bg-gray-50"} ${isToday ? "tw-ring-2 tw-ring-inset tw-ring-blue-400" : ""}`}>
              <div className={`tw-text-xs ${day.isCurrentMonth ? "tw-text-gray-700" : "tw-text-gray-400"} ${isToday ? "tw-font-bold tw-text-blue-700" : ""}`}>{day.date.getDate()}</div>
              <div className="tw-mt-0.5 tw-space-y-0.5">
                {routed.map((e) => 
                  <RouteBlock key={`${e.status}-${e.userId}`} entry={e} dateStr={ds} areaColor={areaColor} compact={true} onOpenRoute={onOpenRoute} />
                )}
                {appts.map((e) => 
                  <RouteBlock key={`${e.status}-${e.userId}`} entry={e} dateStr={ds} areaColor={areaColor} compact={true} onOpenRoute={onOpenRoute} />
                )}
                {available.length > 0 && 
                  <button onClick={() => onMore({ date: ds, entries: available })} className="tw-text-[10px] tw-text-green-700 hover:tw-text-green-900 hover:tw-underline tw-font-medium">
                    +{available.length} available
                  </button>
                }
                {simulated.length > 0 && 
                  <button onClick={() => onMore({ date: ds, entries })} className="tw-text-[10px] tw-text-purple-700 hover:tw-text-purple-900 hover:tw-underline tw-font-medium">
                    ⚡{simulated.length} simulated
                  </button>
                }
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── week ────────────────────────────────────────────────────────────────────
function WeekView({ days, entriesFor, areaColor, todayStr, onOpenRoute }) {
  return (
    <div className="tw-grid tw-grid-cols-7 tw-gap-2">
      {days.map((day, i) => {
        const ds = formatDate(day.date);
        const entries = entriesFor(ds);
        const isToday = ds === todayStr;
        return (
          <div key={i} className={`tw-border tw-rounded tw-min-h-[24rem] ${isToday ? "tw-ring-2 tw-ring-blue-400" : ""}`}>
            <div className={`tw-px-2 tw-py-1.5 tw-border-b tw-text-xs tw-font-medium ${isToday ? "tw-bg-blue-50 tw-text-blue-700" : "tw-bg-gray-50 tw-text-gray-600"}`}>
              {WEEKDAY_SHORT[i]} <span className="tw-text-gray-400">{day.date.getDate()}</span>
            </div>
            <div className="tw-p-1.5 tw-space-y-1.5">
              {entries.length === 0 && <p className="tw-text-xs tw-text-gray-300">—</p>}
              {entries.map((e) => 
                <RouteBlock key={`${e.status}-${e.userId}`} entry={e} dateStr={ds} areaColor={areaColor} compact={false} onOpenRoute={onOpenRoute} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── day ─────────────────────────────────────────────────────────────────────
function DayView({ date, entries, areaColor, todayStr, onOpenRoute }) {
  const ds = formatDate(date);
  const isToday = ds === todayStr;
  return (
    <div className="tw-border tw-rounded tw-max-w-2xl">
      <div className={`tw-px-3 tw-py-2 tw-border-b ${isToday ? "tw-bg-blue-50" : "tw-bg-gray-50"}`}>
        <span className="tw-font-medium">{date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>
      </div>
      <div className="tw-p-3 tw-space-y-2">
        {entries.length === 0 && <p className="tw-text-sm tw-text-gray-400">No inspectors scheduled</p>}
        {entries.map((e) => 
          <RouteBlock key={`${e.status}-${e.userId}`} entry={e} dateStr={ds} areaColor={areaColor} compact={false} onOpenRoute={onOpenRoute} />
        )}
      </div>
    </div>
  );
}